import { useState } from "react";
import { MessageCircle, Mail, Monitor, Loader2 } from "lucide-react";

// Discord icon (lucide-react doesn't ship one; inline SVG matching Discord brand mark)
const DiscordIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3.2a.075.075 0 0 0-.079.037c-.34.604-.717 1.392-.98 2.01a18.27 18.27 0 0 0-5.485 0 12.6 12.6 0 0 0-.995-2.01.078.078 0 0 0-.079-.037 19.736 19.736 0 0 0-3.76 1.169.07.07 0 0 0-.032.027C2.07 8.045 1.43 11.616 1.744 15.144a.083.083 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.029.078.078 0 0 0 .084-.028 14.1 14.1 0 0 0 1.226-1.994.076.076 0 0 0-.041-.105 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.075.075 0 0 1 .077-.01c3.927 1.793 8.18 1.793 12.061 0a.075.075 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.128 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.04.106c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.029.077.077 0 0 0 .032-.054c.5-4.177-.838-7.72-3.548-10.748a.061.061 0 0 0-.031-.028zM8.02 13.331c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.974 0c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import PlatformChatDialog from "./PlatformChatDialog";

interface ContactOptionsPopoverProps {
  anuncioId: string;
  itemNome: string;
  temWhatsapp?: boolean;
  temEmail?: boolean;
  anuncianteId?: string | null;
  anuncianteApelido?: string | null;
  anuncianteAvatarUrl?: string | null;
  preferenciasContato?: string[];
  children: React.ReactNode;
  onRequireLogin?: (action: () => void) => void;
  sessionContato?: string;
  sessionToken?: string;
}

const ContactOptionsPopover = ({
  anuncioId,
  itemNome,
  temWhatsapp,
  temEmail,
  anuncianteId,
  anuncianteApelido,
  anuncianteAvatarUrl,
  preferenciasContato = ["plataforma"],
  children,
  onRequireLogin,
  sessionContato,
  sessionToken,
}: ContactOptionsPopoverProps) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resolvedAnuncianteId, setResolvedAnuncianteId] = useState<string | null>(null);
  const [resolvedApelido, setResolvedApelido] = useState<string | null>(null);
  const [resolvedAvatar, setResolvedAvatar] = useState<string | null>(null);

  const effectiveAnuncianteId = resolvedAnuncianteId || anuncianteId || null;
  const effectiveApelido = resolvedApelido || anuncianteApelido || null;
  const effectiveAvatar = resolvedAvatar || anuncianteAvatarUrl || null;

  const fetchContactAndDo = async (action: "whatsapp" | "email" | "discord") => {
    if (!sessionContato || !sessionToken) {
      toast({ title: "Faça login para ver dados de contato", variant: "destructive" });
      if (onRequireLogin) onRequireLogin(() => fetchContactAndDo(action));
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("get-contact-info", {
        body: {
          anuncio_id: anuncioId,
          contato: sessionContato,
          token: sessionToken,
        },
      });
      if (error || !data?.success) {
        const msg = data?.error || "Erro ao obter contato";
        toast({ title: msg, variant: "destructive" });
        return;
      }
      if (action === "whatsapp") {
        if (!data.contato_telefone) {
          toast({ title: "WhatsApp não disponível", variant: "destructive" });
          return;
        }
        const msg = encodeURIComponent(`Olá! Tenho interesse no item "${itemNome}" anunciado no RPG Coin.`);
        window.open(`https://wa.me/${data.contato_telefone}?text=${msg}`, "_blank");
      } else if (action === "email") {
        if (!data.anunciante_email) {
          toast({ title: "E-mail não disponível", variant: "destructive" });
          return;
        }
        navigator.clipboard.writeText(data.anunciante_email);
        toast({ title: "E-mail copiado!" });
      } else if (action === "discord") {
        const username = data.anunciante_discord_username as string | null;
        const link = data.anunciante_discord_link as string | null;
        if (!username && !link) {
          toast({ title: "Discord não disponível", variant: "destructive" });
          return;
        }
        if (link) {
          window.open(link, "_blank", "noopener,noreferrer");
          if (username) {
            navigator.clipboard.writeText(username).catch(() => undefined);
            toast({ title: `Discord aberto. Usuário copiado: ${username}` });
          } else {
            toast({ title: "Link do Discord aberto" });
          }
        } else if (username) {
          navigator.clipboard.writeText(username);
          toast({ title: `Usuário Discord copiado: ${username}` });
        }
      }
    } catch {
      toast({ title: "Erro ao obter contato", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const resolveAnuncianteAndOpenChat = async () => {
    if (effectiveAnuncianteId) {
      setChatOpen(true);
      return;
    }
    // Try to resolve anunciante from the ad's contact info via backend
    if (!sessionContato || !sessionToken) {
      toast({ title: "Faça login para contatar", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("get-contact-info", {
        body: { anuncio_id: anuncioId, contato: sessionContato, token: sessionToken, mode: "chat" },
      });
      if (error || !data?.success) {
        toast({ title: data?.error || "Anunciante não identificado", variant: "destructive" });
        return;
      }
      if (data.anunciante_perfil_id) {
        setResolvedAnuncianteId(data.anunciante_perfil_id);
        setResolvedApelido(data.anunciante_apelido || null);
        setResolvedAvatar(data.anunciante_avatar_url || null);
        setChatOpen(true);
      } else {
        toast({ title: "Anunciante não identificado", variant: "destructive" });
      }
    } catch {
      toast({ title: "Erro ao identificar anunciante", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const openPlatformChat = () => {
    const doOpen = () => resolveAnuncianteAndOpenChat();
    if (onRequireLogin) {
      onRequireLogin(doOpen);
    } else {
      doOpen();
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    const prefs = preferenciasContato;

    // If only one preference, execute directly
    if (prefs.length === 1) {
      if (prefs[0] === "whatsapp") {
        fetchContactAndDo("whatsapp");
      } else if (prefs[0] === "email") {
        fetchContactAndDo("email");
      } else if (prefs[0] === "discord") {
        fetchContactAndDo("discord");
      } else if (prefs[0] === "plataforma") {
        openPlatformChat();
      }
      return;
    }

    // Multiple options: show popover
    setPopoverOpen(true);
  };

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverAnchor asChild>
          <span
            className="inline-flex items-center justify-center min-w-0"
            onClick={handleClick}
          >
            {loading ? (
              <span className="inline-flex h-9 w-9 items-center justify-center" aria-label="Carregando">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
              </span>
            ) : (
              children
            )}
          </span>
        </PopoverAnchor>
        <PopoverContent
          className="w-auto p-1 tibia-border bg-card z-[100]"
          side="top"
          align="center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col gap-0.5">
            {preferenciasContato.includes("whatsapp") && temWhatsapp && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPopoverOpen(false);
                  fetchContactAndDo("whatsapp");
                }}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-1.5 rounded-sm hover:bg-secondary transition-colors text-left"
              >
                <MessageCircle className="w-3.5 h-3.5 text-green-500" />
                <span className="font-pixel text-[7px]">WhatsApp</span>
              </button>
            )}
            {preferenciasContato.includes("email") && temEmail && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPopoverOpen(false);
                  fetchContactAndDo("email");
                }}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-1.5 rounded-sm hover:bg-secondary transition-colors text-left"
              >
                <Mail className="w-3.5 h-3.5 text-blue-400" />
                <span className="font-pixel text-[7px]">Copiar E-mail</span>
              </button>
            )}
            {preferenciasContato.includes("discord") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPopoverOpen(false);
                  fetchContactAndDo("discord");
                }}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-1.5 rounded-sm hover:bg-secondary transition-colors text-left"
              >
                <DiscordIcon className="w-3.5 h-3.5 text-[#5865F2]" />
                <span className="font-pixel text-[7px]">Discord</span>
              </button>
            )}
            {preferenciasContato.includes("plataforma") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPopoverOpen(false);
                  openPlatformChat();
                }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-sm hover:bg-secondary transition-colors text-left"
              >
                <Monitor className="w-3.5 h-3.5 text-primary" />
                <span className="font-pixel text-[7px]">Chat Plataforma</span>
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {effectiveAnuncianteId && (
        <div onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <PlatformChatDialog
            open={chatOpen}
            onOpenChange={setChatOpen}
            destinatarioId={effectiveAnuncianteId}
            destinatarioApelido={effectiveApelido || "Anunciante"}
            destinatarioAvatar={effectiveAvatar}
            anuncioId={anuncioId}
            itemNome={itemNome}
          />
        </div>
      )}
    </>
  );
};

export default ContactOptionsPopover;
